/**
 * ConstructorA4 (Конструктор А4) — full-page batch compositor.
 * Supports multiple templates with customizable dimensions.
 * Left: template selector + thumbnail preview + file uploads.
 * Right: editor canvas + transform controls (shown on click).
 * Assets stored in private S3 bucket via presigned URLs.
 */
import { useState, useEffect, useRef, useCallback, type FC } from 'react';
import { Icon } from './Icon';
import { apiFetch, apiPost, apiPostForm } from '../services/api';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

interface Props {
  onBack: () => void;
}

interface Template {
  id: string;
  name: string;
  width: number;
  height: number;
  transform: { x: number; y: number; scale: number; rotation: number };
  renderRotation: number;
  compressJpg: boolean;
}

interface CompletedFile {
  name: string;
  timestamp: number;
  file: File;
}

const DEFAULT_TEMPLATE: Template = {
  id: 'tpl_default',
  name: 'A4',
  width: 2480,
  height: 3508,
  transform: { x: 2480 / 2, y: 3508 / 2, scale: 1, rotation: 0 },
  renderRotation: 0,
  compressJpg: true,
};

export const ConstructorA4: FC<Props> = ({ onBack }) => {
  /* ── Template system ── */
  const [templates, setTemplates] = useState<Template[]>([{ ...DEFAULT_TEMPLATE }]);
  const [activeTemplateId, setActiveTemplateId] = useState(DEFAULT_TEMPLATE.id);
  const [showNewTplDialog, setShowNewTplDialog] = useState(false);
  const [newTplName, setNewTplName] = useState('');
  const [newTplWidth, setNewTplWidth] = useState(2480);
  const [newTplHeight, setNewTplHeight] = useState(3508);

  const activeTemplate = templates.find((t) => t.id === activeTemplateId) || templates[0];

  /* ── Per-template state ── */
  const [frameImage, setFrameImage] = useState<string | null>(null);
  const [sampleImage, setSampleImage] = useState<string | null>(null);
  const [transform, setTransform] = useState({ ...DEFAULT_TEMPLATE.transform });
  const [renderRotation, setRenderRotation] = useState(0);
  const [compressJpg, setCompressJpg] = useState(true);

  /* ── UI state ── */
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, text: '' });
  const [isDragOver, setIsDragOver] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [completedFiles, setCompletedFiles] = useState<CompletedFile[]>([]);

  /* ── Refs ── */
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const editorCanvasRef = useRef<HTMLCanvasElement>(null);
  const transformRef = useRef(transform);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const frameImgRef = useRef<HTMLImageElement | null>(null);
  const sampleImgRef = useRef<HTMLImageElement | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeTemplateIdRef = useRef(activeTemplateId);
  const templatesRef = useRef(templates);

  useEffect(() => { activeTemplateIdRef.current = activeTemplateId; }, [activeTemplateId]);
  useEffect(() => { templatesRef.current = templates; }, [templates]);

  const loadImg = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  /* ── Asset URL helpers ── */
  const fetchAssetUrl = async (type: 'frame' | 'sample', templateId?: string): Promise<string | null> => {
    try {
      const tplId = templateId || activeTemplateIdRef.current;
      const data = await apiFetch<{ success: boolean; url: string | null }>(
        `/api/constructor/asset-url?type=${type}&templateId=${encodeURIComponent(tplId)}`,
      );
      return data.success ? data.url : null;
    } catch {
      return null;
    }
  };

  const loadAssetsForTemplate = async (templateId: string) => {
    setAssetsLoading(true);
    frameImgRef.current = null;
    sampleImgRef.current = null;
    const [frameUrl, sampleUrl] = await Promise.all([
      fetchAssetUrl('frame', templateId),
      fetchAssetUrl('sample', templateId),
    ]);
    if (frameUrl) {
      try {
        const img = await loadImg(frameUrl);
        frameImgRef.current = img;
        setFrameImage(frameUrl);
      } catch { setFrameImage(null); }
    } else {
      setFrameImage(null);
    }
    if (sampleUrl) {
      try {
        const img = await loadImg(sampleUrl);
        sampleImgRef.current = img;
        setSampleImage(sampleUrl);
      } catch { setSampleImage(null); }
    } else {
      setSampleImage(null);
    }
    setAssetsLoading(false);
  };

  const loadConfigFromServer = async () => {
    try {
      const data = await apiFetch<{ success: boolean; config: any }>('/api/constructor/config');
      if (data.success && data.config) {
        const c = data.config;
        if (c.templates && Array.isArray(c.templates) && c.templates.length > 0) {
          setTemplates(c.templates);
          templatesRef.current = c.templates;
          const aId = c.activeTemplateId || c.templates[0].id;
          setActiveTemplateId(aId);
          activeTemplateIdRef.current = aId;
          const tpl = c.templates.find((tp: Template) => tp.id === aId) || c.templates[0];
          setTransform(tpl.transform);
          setRenderRotation(tpl.renderRotation ?? 0);
          setCompressJpg(tpl.compressJpg ?? true);
          await loadAssetsForTemplate(aId);
        } else if (c.transform) {
          const legacy: Template = {
            ...DEFAULT_TEMPLATE,
            transform: c.transform,
            renderRotation: c.renderRotation ?? 0,
            compressJpg: true,
          };
          setTemplates([legacy]);
          templatesRef.current = [legacy];
          setActiveTemplateId(legacy.id);
          activeTemplateIdRef.current = legacy.id;
          setTransform(legacy.transform);
          setRenderRotation(legacy.renderRotation);
          await loadAssetsForTemplate(legacy.id);
        } else {
          await loadAssetsForTemplate(activeTemplateIdRef.current);
        }
      } else {
        await loadAssetsForTemplate(activeTemplateIdRef.current);
      }
    } catch (e) {
      console.error('Failed to load constructor config:', e);
    } finally {
      setConfigLoaded(true);
    }
  };

  const saveConfigToServer = useCallback(
    async (tpls?: Template[], aId?: string, t?: typeof transform, rot?: number, jpg?: boolean) => {
      setSaving(true);
      try {
        const tplsToSave = tpls || templatesRef.current;
        const activeId = aId || activeTemplateIdRef.current;
        const curTransform = t || transformRef.current;
        const curRot = rot != null ? rot : renderRotation;
        const curJpg = jpg != null ? jpg : compressJpg;
        const updatedTpls = tplsToSave.map((tpl) =>
          tpl.id === activeId
            ? { ...tpl, transform: curTransform, renderRotation: curRot, compressJpg: curJpg }
            : tpl,
        );
        await apiPost('/api/constructor/config', {
          config: { templates: updatedTpls, activeTemplateId: activeId },
        });
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    },
    [renderRotation, compressJpg],
  );

  const scheduleSave = useCallback(
    (t?: typeof transform, rot?: number, jpg?: boolean) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveConfigToServer(undefined, undefined, t, rot, jpg), 800);
    },
    [saveConfigToServer],
  );

  /* ── Template management ── */
  const switchTemplate = useCallback(
    async (newId: string) => {
      if (newId === activeTemplateIdRef.current) return;
      setTemplates((prev) => {
        const updated = prev.map((tp) =>
          tp.id === activeTemplateIdRef.current
            ? { ...tp, transform: transformRef.current, renderRotation, compressJpg }
            : tp,
        );
        templatesRef.current = updated;
        return updated;
      });
      setActiveTemplateId(newId);
      activeTemplateIdRef.current = newId;
      const tpl = templatesRef.current.find((tp) => tp.id === newId);
      if (tpl) {
        setTransform(tpl.transform);
        transformRef.current = tpl.transform;
        setRenderRotation(tpl.renderRotation);
        setCompressJpg(tpl.compressJpg);
      }
      setFrameImage(null);
      setSampleImage(null);
      await loadAssetsForTemplate(newId);
    },
    [renderRotation, compressJpg],
  );

  const createTemplate = useCallback(() => {
    const id = 'tpl_' + Date.now();
    const name = newTplName.trim() || `${newTplWidth}×${newTplHeight}`;
    const tpl: Template = {
      id,
      name,
      width: newTplWidth,
      height: newTplHeight,
      transform: { x: newTplWidth / 2, y: newTplHeight / 2, scale: 1, rotation: 0 },
      renderRotation: 0,
      compressJpg: true,
    };
    const updatedTemplates = templatesRef.current.map((tp) =>
      tp.id === activeTemplateIdRef.current
        ? { ...tp, transform: transformRef.current, renderRotation, compressJpg }
        : tp,
    );
    const newTemplates = [...updatedTemplates, tpl];
    setTemplates(newTemplates);
    templatesRef.current = newTemplates;
    setActiveTemplateId(id);
    activeTemplateIdRef.current = id;
    setTransform(tpl.transform);
    transformRef.current = tpl.transform;
    setRenderRotation(0);
    setCompressJpg(true);
    setFrameImage(null);
    setSampleImage(null);
    frameImgRef.current = null;
    sampleImgRef.current = null;
    setShowNewTplDialog(false);
    setNewTplName('');
    setNewTplWidth(2480);
    setNewTplHeight(3508);
    saveConfigToServer(newTemplates, id, tpl.transform, 0, true);
  }, [newTplName, newTplWidth, newTplHeight, renderRotation, compressJpg, saveConfigToServer]);

  const deleteTemplate = useCallback(
    (id: string) => {
      if (templatesRef.current.length <= 1) return;
      const newTemplates = templatesRef.current.filter((tp) => tp.id !== id);
      setTemplates(newTemplates);
      templatesRef.current = newTemplates;
      if (activeTemplateIdRef.current === id) {
        const first = newTemplates[0];
        setActiveTemplateId(first.id);
        activeTemplateIdRef.current = first.id;
        setTransform(first.transform);
        transformRef.current = first.transform;
        setRenderRotation(first.renderRotation);
        setCompressJpg(first.compressJpg);
        loadAssetsForTemplate(first.id);
      }
      saveConfigToServer(newTemplates, activeTemplateIdRef.current);
    },
    [saveConfigToServer],
  );

  useEffect(() => {
    if (!configLoaded) loadConfigFromServer();
  }, [configLoaded]);
  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  useEffect(() => {
    if (frameImage && !frameImgRef.current) {
      loadImg(frameImage)
        .then((img) => { frameImgRef.current = img; })
        .catch(() => { frameImgRef.current = null; });
    } else if (!frameImage) {
      frameImgRef.current = null;
    }
  }, [frameImage]);
  useEffect(() => {
    if (sampleImage && !sampleImgRef.current) {
      loadImg(sampleImage)
        .then((img) => { sampleImgRef.current = img; })
        .catch(() => { sampleImgRef.current = null; });
    } else if (!sampleImage) {
      sampleImgRef.current = null;
    }
  }, [sampleImage]);

  const drawComposite = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      bgImg: HTMLImageElement | null,
      fImg: HTMLImageElement | null,
      t: typeof transform,
    ) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      if (bgImg) {
        ctx.save();
        ctx.translate(t.x, t.y);
        ctx.rotate((t.rotation * Math.PI) / 180);
        ctx.scale(t.scale, t.scale);
        ctx.drawImage(bgImg, -bgImg.width / 2, -bgImg.height / 2);
        ctx.restore();
      } else {
        const isLight = document.documentElement.getAttribute('data-theme') === 'light';
        ctx.fillStyle = isLight ? '#f1f5f9' : '#1e293b';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
      if (fImg) ctx.drawImage(fImg, 0, 0, ctx.canvas.width, ctx.canvas.height);
    },
    [],
  );

  const applyRenderRotation = useCallback(async (sourceCanvas: HTMLCanvasElement, rot: number) => {
    const toBlob = (cvs: HTMLCanvasElement) => {
      if (compressJpg) {
        const tmp = document.createElement('canvas');
        tmp.width = cvs.width;
        tmp.height = cvs.height;
        const tCtx = tmp.getContext('2d')!;
        tCtx.fillStyle = '#ffffff';
        tCtx.fillRect(0, 0, tmp.width, tmp.height);
        tCtx.drawImage(cvs, 0, 0);
        return new Promise<Blob | null>((r) => tmp.toBlob(r, 'image/jpeg', 0.92));
      }
      return new Promise<Blob | null>((r) => cvs.toBlob(r, 'image/png'));
    };
    if (!rot || (rot !== 90 && rot !== -90)) return toBlob(sourceCanvas);
    const w = sourceCanvas.width;
    const h = sourceCanvas.height;
    const rc = document.createElement('canvas');
    rc.width = h;
    rc.height = w;
    const rCtx = rc.getContext('2d')!;
    rCtx.translate(rc.width / 2, rc.height / 2);
    rCtx.rotate((rot * Math.PI) / 180);
    rCtx.drawImage(sourceCanvas, -w / 2, -h / 2);
    return toBlob(rc);
  }, [compressJpg]);

  // Render loop — thumbnail canvas (left panel)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = activeTemplate.width;
    canvas.height = activeTemplate.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let rafId: number;
    const render = () => {
      drawComposite(ctx, sampleImgRef.current, frameImgRef.current, transformRef.current);
      rafId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(rafId);
  }, [frameImage, sampleImage, drawComposite, activeTemplate.width, activeTemplate.height]);

  // Render loop — editor canvas (right panel, when open)
  useEffect(() => {
    if (!editorOpen) return;
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    canvas.width = activeTemplate.width;
    canvas.height = activeTemplate.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let rafId: number;
    const render = () => {
      drawComposite(ctx, sampleImgRef.current, frameImgRef.current, transformRef.current);
      rafId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(rafId);
  }, [editorOpen, frameImage, sampleImage, drawComposite, activeTemplate.width, activeTemplate.height]);

  // Upload handlers — upload to S3 via server
  const uploadAsset = async (file: File, assetType: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('assetType', assetType);
    fd.append('templateId', activeTemplateIdRef.current);
    const res = await apiPostForm<{ success: boolean; url: string; s3Key: string }>(
      '/api/constructor/upload-asset',
      fd,
    );
    if (res.success) return res.url;
    throw new Error('Upload failed');
  };

  const handleFrameUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAssetsLoading(true);
    try {
      const localUrl = await readFileAsDataUrl(file);
      const img = await loadImg(localUrl);
      frameImgRef.current = img;
      setFrameImage(localUrl);
      const url = await uploadAsset(file, 'frame');
      const srvImg = await loadImg(url);
      frameImgRef.current = srvImg;
      setFrameImage(url);
      setTimeout(() => saveConfigToServer(), 300);
    } catch (err) {
      console.error(err);
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleSampleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAssetsLoading(true);
    try {
      const localUrl = await readFileAsDataUrl(file);
      const img = await loadImg(localUrl);
      sampleImgRef.current = img;
      setSampleImage(localUrl);
      const url = await uploadAsset(file, 'sample');
      const srvImg = await loadImg(url);
      sampleImgRef.current = srvImg;
      setSampleImage(url);
      setTimeout(() => saveConfigToServer(), 300);
    } catch (err) {
      console.error(err);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Canvas drag (editor canvas)
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sampleImage) return;
    isDraggingRef.current = true;
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    lastMouseRef.current = { x: cx, y: cy };
  };
  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingRef.current || !sampleImage) return;
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const bounds = canvas.getBoundingClientRect();
    const aT = templatesRef.current.find((tp) => tp.id === activeTemplateIdRef.current) || templatesRef.current[0];
    const dx = (cx - lastMouseRef.current.x) * (aT.width / bounds.width);
    const dy = (cy - lastMouseRef.current.y) * (aT.height / bounds.height);
    transformRef.current = {
      ...transformRef.current,
      x: transformRef.current.x + dx,
      y: transformRef.current.y + dy,
    };
    lastMouseRef.current = { x: cx, y: cy };
  };
  const handleMouseUp = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      const newT = { ...transformRef.current };
      setTransform(newT);
      scheduleSave(newT, renderRotation);
    }
  };

  // File handlers
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setFiles((prev) => [
      ...prev,
      ...Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/')),
    ]);
  }, []);
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files)
      setFiles((prev) => [
        ...prev,
        ...Array.from(e.target.files!).filter((f) => f.type.startsWith('image/')),
      ]);
  }, []);
  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const cycleRotation = () =>
    setRenderRotation((prev) => {
      const next = prev === 0 ? 90 : prev === 90 ? -90 : 0;
      scheduleSave(transform, next);
      return next;
    });

  // Transform update helper
  const updateTransform = useCallback(
    (newT: typeof transform) => {
      setTransform(newT);
      transformRef.current = newT;
      scheduleSave(newT, renderRotation);
    },
    [renderRotation, scheduleSave],
  );

  // Process batch
  const handleProcess = async () => {
    if (!frameImage || files.length === 0) return;
    setProcessing(true);
    setProgress({ current: 0, total: files.length, text: 'Инициализация...' });
    const ext = compressJpg ? '.jpg' : '.png';
    try {
      const fImg = await loadImg(frameImage);
      const canvas = document.createElement('canvas');
      canvas.width = activeTemplate.width;
      canvas.height = activeTemplate.height;
      const ctx = canvas.getContext('2d')!;

      if (files.length === 1) {
        setProgress({ current: 1, total: 1, text: 'Обработка...' });
        const contentImg = await loadImg(await readFileAsDataUrl(files[0]));
        drawComposite(ctx, contentImg, fImg, transform);
        const blob = await applyRenderRotation(canvas, renderRotation);
        if (blob) saveAs(blob, files[0].name.replace(/\.[^/.]+$/, '') + '_print' + ext);
      } else {
        const zip = new JSZip();
        for (let i = 0; i < files.length; i++) {
          setProgress({
            current: i + 1,
            total: files.length,
            text: `Обработка ${files[i].name}...`,
          });
          try {
            const contentImg = await loadImg(await readFileAsDataUrl(files[i]));
            drawComposite(ctx, contentImg, fImg, transform);
            const blob = await applyRenderRotation(canvas, renderRotation);
            if (blob) zip.file(files[i].name.replace(/\.[^/.]+$/, '') + '_print' + ext, blob);
          } catch (err) {
            console.error('Error processing', files[i].name, err);
          }
        }
        setProgress({ current: files.length, total: files.length, text: 'Упаковка в ZIP...' });
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'constructor_a4.zip');
      }
      // Move files to completed
      const now = Date.now();
      setCompletedFiles((prev) => [
        ...files.map((f) => ({ name: f.name, timestamp: now, file: f })),
        ...prev,
      ]);
      setFiles([]);
      setProgress((prev) => ({ ...prev, text: 'Готово! Скачивание началось.' }));
    } catch (err) {
      console.error(err);
      setProgress((prev) => ({ ...prev, text: 'Ошибка при обработке.' }));
    } finally {
      setProcessing(false);
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
  const t = transform;
  const W = activeTemplate.width;
  const H = activeTemplate.height;

  const returnFileToExport = (idx: number) => {
    const cf = completedFiles[idx];
    if (!cf) return;
    setFiles((prev) => [...prev, cf.file]);
    setCompletedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
        >
          <Icon name="arrow-left" className="w-5 h-5" />
        </button>
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-lg">
          <Icon name="layout-template" className="w-4 h-4 text-white" />
        </div>
        <h2 className="text-base font-bold text-white">Конструктор А4</h2>
        {saving && (
          <span className="text-xs text-blue-400 flex items-center gap-1 ml-2">
            <Icon name="loader-2" className="w-3 h-3 animate-spin" />
            Сохранение...
          </span>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* ═══ LEFT COLUMN: Template Selector + Thumbnail + Controls ═══ */}
        <div className="w-full lg:w-80 flex flex-col gap-3 flex-shrink-0">
          {/* Template Selector */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-white flex items-center gap-2">
                <Icon name="layers" className="w-3.5 h-3.5 text-blue-400" />
                Макеты
              </h3>
              <button
                onClick={() => setShowNewTplDialog(true)}
                className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                <Icon name="plus" className="w-3 h-3" />
                Создать новый
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((tpl) => (
                <div key={tpl.id} className="relative group">
                  <button
                    onClick={() => switchTemplate(tpl.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      tpl.id === activeTemplateId
                        ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                        : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                    }`}
                  >
                    {tpl.name}
                    <span className="ml-1 text-[9px] opacity-60">{tpl.width}×{tpl.height}</span>
                  </button>
                  {templates.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.id); }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Удалить"
                    >
                      <Icon name="x" className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* New Template Dialog */}
          {showNewTplDialog && (
            <div className="bg-slate-800 rounded-xl border border-blue-500/50 p-3">
              <h4 className="text-xs font-semibold text-white mb-2">Новый макет</h4>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  placeholder="Название (необязательно)"
                  value={newTplName}
                  onChange={(e) => setNewTplName(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Ширина (px)</label>
                    <input
                      type="number"
                      value={newTplWidth}
                      onChange={(e) => setNewTplWidth(Math.max(100, parseInt(e.target.value) || 100))}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white focus:border-blue-500 outline-none no-spinner"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-0.5">Высота (px)</label>
                    <input
                      type="number"
                      value={newTplHeight}
                      onChange={(e) => setNewTplHeight(Math.max(100, parseInt(e.target.value) || 100))}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white focus:border-blue-500 outline-none no-spinner"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createTemplate}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"
                  >
                    Создать
                  </button>
                  <button
                    onClick={() => setShowNewTplDialog(false)}
                    className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:text-white text-xs transition-colors"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Thumbnail Preview */}
          <div
            className="bg-slate-800 rounded-xl border border-slate-700 p-3 cursor-pointer hover:border-blue-500/50 transition-colors group"
            onClick={() => setEditorOpen(true)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-white flex items-center gap-2">
                <Icon name="eye" className="w-3.5 h-3.5 text-blue-400" />
                Превью
              </h3>
              <span className="text-[10px] text-slate-500 group-hover:text-blue-400 transition-colors flex items-center gap-1">
                <Icon name="maximize-2" className="w-3 h-3" />
                Открыть редактор
              </span>
            </div>
            <div
              className="relative bg-white rounded-lg overflow-hidden shadow-inner"
              style={{ aspectRatio: `${W}/${H}` }}
            >
              <canvas
                ref={canvasRef}
                width={W}
                height={H}
                className="w-full h-full object-contain block"
              />
              {assetsLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm z-10">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="loader-2" className="w-6 h-6 text-blue-400 animate-spin" />
                    <span className="text-xs text-white/70">Загрузка...</span>
                  </div>
                </div>
              )}
              {!assetsLoading && !frameImage && !sampleImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50">
                  <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
                    {activeTemplate.name} Превью
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Frame Upload */}
          <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
            <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
              <Icon name="image" className="w-3.5 h-3.5 text-blue-400" />
              Фрейм (передний план)
            </h3>
            <div className="relative cursor-pointer border-2 border-dashed border-slate-600 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-900/10 transition-colors text-center">
              <input
                type="file"
                accept="image/png"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleFrameUpload}
              />
              {frameImage ? (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <Icon name="check-circle" className="w-5 h-5" />
                  <span className="text-sm font-medium">Загружен</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-slate-400">
                  <Icon name="upload" className="w-5 h-5" />
                  <span className="text-sm">PNG с прозрачностью</span>
                </div>
              )}
            </div>
          </div>

          {/* Sample Upload */}
          <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
            <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
              <Icon name="image" className="w-3.5 h-3.5 text-blue-400" />
              Сэмпл (задний фон)
            </h3>
            <div className="relative cursor-pointer border-2 border-dashed border-slate-600 rounded-lg p-3 hover:border-blue-500 hover:bg-blue-900/10 transition-colors text-center">
              <input
                type="file"
                accept="image/*"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleSampleUpload}
              />
              {sampleImage ? (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <Icon name="check-circle" className="w-5 h-5" />
                  <span className="text-sm font-medium">Загружен</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-slate-400">
                  <Icon name="upload" className="w-5 h-5" />
                  <span className="text-sm">Для настройки позиции</span>
                </div>
              )}
            </div>
          </div>

          {/* Render Rotation */}
          <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
            <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
              <Icon name="rotate-cw" className="w-3.5 h-3.5 text-blue-400" />
              Поворот после рендера
            </h3>
            <button
              onClick={cycleRotation}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors ${renderRotation ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'}`}
            >
              <Icon name="rotate-cw" className="w-4 h-4" />
              {renderRotation === 90 ? '+90°' : renderRotation === -90 ? '-90°' : '0° (выкл)'}
            </button>
          </div>

          <p className="text-[11px] text-slate-500 px-1">
            Нажмите на превью чтобы открыть редактор позиционирования.
          </p>
        </div>

        {/* ═══ RIGHT COLUMN: Editor + Batch Files + Completed ═══ */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {editorOpen && (
            /* ── Editor Panel ── */
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700 bg-slate-800/80">
                <h3 className="text-xs font-semibold text-white flex items-center gap-2">
                  <Icon name="move" className="w-3.5 h-3.5 text-indigo-400" />
                  Редактор позиции
                </h3>
                <button
                  onClick={() => setEditorOpen(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                >
                  <Icon name="x" className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-col xl:flex-row gap-0">
                {/* Canvas */}
                <div
                  className="flex-1 bg-slate-900/50 flex items-center justify-center p-4 overflow-hidden select-none min-h-[400px]"
                  style={{ touchAction: 'none' }}
                >
                  <div
                    className="relative shadow-2xl bg-white ring-1 ring-slate-900/10"
                    style={{
                      aspectRatio: `${W}/${H}`,
                      maxHeight: '70vh',
                      maxWidth: '100%',
                    }}
                  >
                    <canvas
                      ref={editorCanvasRef}
                      width={W}
                      height={H}
                      className="w-full h-full object-contain cursor-move block"
                      style={{ touchAction: 'none' }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={handleMouseDown}
                      onTouchMove={handleMouseMove}
                      onTouchEnd={handleMouseUp}
                    />
                  </div>
                </div>

                {/* Transform Panel (mockup-style) */}
                <div className="w-full xl:w-72 border-t xl:border-t-0 xl:border-l border-slate-700 bg-slate-800/60 p-4 flex flex-col gap-4">
                  <h4 className="text-xs font-semibold text-white flex items-center gap-2">
                    <Icon name="move" className="w-3.5 h-3.5 text-indigo-400" />
                    Трансформация
                  </h4>

                  {/* X/Y Controls */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="tp-numeric-field">
                      <span className="tp-numeric-label">X</span>
                      <div className="tp-stepper">
                        <button onClick={() => updateTransform({ ...t, x: t.x - 10 })} className="tp-stepper-btn">
                          <Icon name="minus" className="w-3 h-3" />
                        </button>
                        <div className="tp-stepper-value">
                          <input
                            type="number"
                            value={Math.round(t.x)}
                            onChange={(e) => updateTransform({ ...t, x: parseFloat(e.target.value) || 0 })}
                            className="tp-stepper-input no-spinner w-12"
                          />
                          <span className="tp-stepper-unit">px</span>
                        </div>
                        <button onClick={() => updateTransform({ ...t, x: t.x + 10 })} className="tp-stepper-btn">
                          <Icon name="plus" className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="tp-numeric-field">
                      <span className="tp-numeric-label">Y</span>
                      <div className="tp-stepper">
                        <button onClick={() => updateTransform({ ...t, y: t.y - 10 })} className="tp-stepper-btn">
                          <Icon name="minus" className="w-3 h-3" />
                        </button>
                        <div className="tp-stepper-value">
                          <input
                            type="number"
                            value={Math.round(t.y)}
                            onChange={(e) => updateTransform({ ...t, y: parseFloat(e.target.value) || 0 })}
                            className="tp-stepper-input no-spinner w-12"
                          />
                          <span className="tp-stepper-unit">px</span>
                        </div>
                        <button onClick={() => updateTransform({ ...t, y: t.y + 10 })} className="tp-stepper-btn">
                          <Icon name="plus" className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Scale */}
                  <div className="tp-numeric-field">
                    <span className="tp-numeric-label">Масштаб</span>
                    <div className="tp-stepper">
                      <button
                        onClick={() => updateTransform({ ...t, scale: Math.max(0.05, t.scale - 0.01) })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="minus" className="w-3 h-3" />
                      </button>
                      <div className="tp-stepper-value">
                        <input
                          type="number"
                          value={Math.round(t.scale * 100)}
                          onChange={(e) =>
                            updateTransform({ ...t, scale: Math.min(10, Math.max(0.05, parseFloat(e.target.value) / 100 || 0.05)) })
                          }
                          className="tp-stepper-input no-spinner w-14"
                        />
                        <span className="tp-stepper-unit">%</span>
                      </div>
                      <button
                        onClick={() => updateTransform({ ...t, scale: Math.min(10, t.scale + 0.01) })}
                        className="tp-stepper-btn"
                      >
                        <Icon name="plus" className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.05"
                    max="3"
                    step="0.01"
                    value={t.scale}
                    onChange={(e) => updateTransform({ ...t, scale: parseFloat(e.target.value) })}
                    className="tp-range-slider"
                  />

                  {/* Rotation */}
                  <div className="tp-numeric-field">
                    <span className="tp-numeric-label">Поворот</span>
                    <div className="tp-stepper">
                      <button onClick={() => updateTransform({ ...t, rotation: t.rotation - 1 })} className="tp-stepper-btn">
                        <Icon name="minus" className="w-3 h-3" />
                      </button>
                      <div className="tp-stepper-value">
                        <input
                          type="number"
                          value={Math.round(t.rotation)}
                          onChange={(e) => updateTransform({ ...t, rotation: parseFloat(e.target.value) || 0 })}
                          className="tp-stepper-input no-spinner w-10"
                        />
                        <span className="tp-stepper-unit">°</span>
                      </div>
                      <button onClick={() => updateTransform({ ...t, rotation: t.rotation + 1 })} className="tp-stepper-btn">
                        <Icon name="plus" className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Quick Rotation Buttons */}
                  <div className="tp-quick-rotate">
                    {[
                      [-90, 'rotate-ccw'],
                      [-45, 'rotate-ccw'],
                      [0, 'refresh-ccw'],
                      [45, 'rotate-cw'],
                      [90, 'rotate-cw'],
                    ].map(([deg, icon]) => (
                      <button
                        key={deg as number}
                        onClick={() =>
                          deg === 0
                            ? updateTransform({ ...t, rotation: 0 })
                            : updateTransform({ ...t, rotation: t.rotation + (deg as number) })
                        }
                        className={`tp-rotate-btn ${deg === 0 ? 'tp-rotate-reset' : ''}`}
                        title={`${deg}°`}
                      >
                        <Icon name={icon as string} className="w-3.5 h-3.5" />
                        {deg !== 0 && (
                          <span>
                            {(deg as number) > 0 ? '+' : ''}
                            {deg}°
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="tp-action-row">
                    <button
                      onClick={() => updateTransform({ ...t, x: W / 2, y: H / 2 })}
                      className="tp-action-btn"
                      title="Центрировать"
                    >
                      <Icon name="crosshair" className="w-3.5 h-3.5" />
                      <span>Центр</span>
                    </button>
                    <button
                      onClick={() => updateTransform({ x: W / 2, y: H / 2, scale: 1, rotation: 0 })}
                      className="tp-action-btn tp-action-danger"
                      title="Сбросить всё"
                    >
                      <Icon name="x-circle" className="w-3.5 h-3.5" />
                      <span>Сброс</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Batch Files Area ── */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {/* Drop Area */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              className={`p-5 border-b border-slate-700 text-center transition-colors cursor-pointer relative ${isDragOver ? 'bg-blue-900/20 border-blue-500' : 'bg-slate-900/30 hover:bg-slate-900/50'} ${processing ? 'pointer-events-none opacity-50' : ''}`}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleFileInput}
                disabled={processing}
              />
              <div className="flex flex-col items-center justify-center space-y-1">
                <div className="p-2 bg-slate-800 rounded-full border border-slate-700">
                  <Icon name="archive" className="w-5 h-5 text-blue-400" />
                </div>
                <p className="text-sm font-medium text-white">
                  {isDragOver ? 'Отпустите файлы...' : 'Перетащите картинки сюда'}
                </p>
                <p className="text-xs text-slate-500">или нажмите для выбора</p>
              </div>
            </div>

            {files.length > 0 && (
              <div className="p-3 max-h-44 overflow-y-auto">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="group relative bg-slate-900 p-2 rounded-lg border border-slate-700 flex flex-col items-center"
                    >
                      {!processing && (
                        <button
                          onClick={() => removeFile(idx)}
                          className="absolute top-1 right-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                        >
                          ×
                        </button>
                      )}
                      <div className="w-7 h-7 bg-slate-800 rounded flex items-center justify-center mb-1 border border-slate-700">
                        <Icon name="file-image" className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                      <span
                        className="text-[10px] text-center text-slate-300 truncate w-full"
                        title={file.name}
                      >
                        {file.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-3 bg-slate-800 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {processing && (
                  <div className="flex items-center gap-3 w-full sm:w-64">
                    <Icon
                      name="loader-2"
                      className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-white">{progress.text}</span>
                        <span className="text-slate-400">{pct}%</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!processing && files.length > 0 && (
                  <span className="text-xs text-slate-400">
                    {files.length} файл(ов)
                    {renderRotation
                      ? ` · поворот ${renderRotation > 0 ? '+' : ''}${renderRotation}°`
                      : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {/* JPG/PNG Toggle */}
                <button
                  onClick={() => {
                    const next = !compressJpg;
                    setCompressJpg(next);
                    scheduleSave(transform, renderRotation, next);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    compressJpg
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                      : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                  }`}
                  title={compressJpg ? 'Экспорт в JPG (сжатие)' : 'Экспорт в PNG (без сжатия)'}
                >
                  <Icon name="file-image" className="w-3.5 h-3.5" />
                  {compressJpg ? 'JPG' : 'PNG'}
                </button>
                <button
                  onClick={() => setFiles([])}
                  disabled={!files.length || processing}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg text-xs font-medium border border-slate-600 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors disabled:opacity-40"
                >
                  Очистить
                </button>
                <button
                  onClick={handleProcess}
                  disabled={!files.length || !frameImage || processing}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-xs font-medium transition-all ${files.length && frameImage && !processing ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}
                >
                  <Icon name="download" className="w-3.5 h-3.5" />
                  {processing ? 'Обработка...' : 'Экспорт'}
                </button>
              </div>
            </div>
          </div>

          {/* ── Completed Files (Выполнено) ── */}
          {completedFiles.length > 0 && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-white flex items-center gap-2">
                  <Icon name="check-circle" className="w-3.5 h-3.5 text-emerald-400" />
                  Выполнено
                  <span className="text-[10px] text-slate-500 font-normal">({completedFiles.length})</span>
                </h3>
                <button
                  onClick={() => setCompletedFiles([])}
                  className="text-[10px] text-slate-500 hover:text-red-400 transition-colors"
                >
                  Очистить историю
                </button>
              </div>
              <div className="p-3 max-h-36 overflow-y-auto">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                  {completedFiles.map((cf, idx) => (
                    <div
                      key={`${cf.name}-${cf.timestamp}-${idx}`}
                      className="group relative bg-slate-900/50 p-2 rounded-lg border border-slate-700/50 flex flex-col items-center"
                    >
                      <button
                        onClick={() => returnFileToExport(idx)}
                        className="absolute top-1 right-1 text-slate-500 hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Вернуть в экспорт"
                      >
                        <Icon name="undo-2" className="w-3 h-3" />
                      </button>
                      <div className="w-7 h-7 bg-emerald-500/10 rounded flex items-center justify-center mb-1 border border-emerald-500/20">
                        <Icon name="check" className="w-3.5 h-3.5 text-emerald-400" />
                      </div>
                      <span
                        className="text-[10px] text-center text-slate-400 truncate w-full"
                        title={cf.name}
                      >
                        {cf.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
